import React, {MutableRefObject, useEffect, useRef, useState} from 'react';
import {DocumentRead} from '../../../../schemas';
import {getPdfUrl} from "../../../../api";
import {PageChangeEvent, ScrollMode, Viewer} from '@react-pdf-viewer/core';
import {OnHighlightKeyword} from '@react-pdf-viewer/search';
import {pdfjs} from "react-pdf";
import {defaultLayoutPlugin} from "@react-pdf-viewer/default-layout";
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import {HighlightOption} from "../../DocumentPageContainerView";
import {withApiErrorHandlerThrow} from "../../../../error_handling";
import {useNavigationContext} from "../../../../contexts/NavContext/nav-context";
import {SpecificComponentLoader} from '../../../../components/Loader';
import {PdfSearchHandle} from "../../DocumentPageContainerView";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const ESCAPE_RE = /[.*+?^${}()|[\]\\]/g;
const escapeRe = (s: string) => s.replace(ESCAPE_RE, "\\$&");

/**
 * Build a regex that tolerates whitespace/invisible-char differences between
 * the OCR body text and the PDF text layer.
 *
 * Splits the query into word-runs and individual punctuation tokens, then
 * joins them with an optional-whitespace seam. This handles cases where the
 * PDF text layer spaces punctuation differently than the OCR text, or joins
 * lines with no separator at all.
 */
export function buildSeamTolerantRegex(query: string): RegExp | null {
    const tokens = (query.trim().match(/[a-zA-Z0-9]+|[^\s]/g) ?? [])
        .map(escapeRe)
        .filter(Boolean);
    if (!tokens.length) return null;
    const seam = String.raw`(?:[\s\u00A0\u2000-\u200B\u2028\u2029\u2060\uFEFF\uFFFE\u00AD\-]*)`;
    const pattern = tokens.join(seam);
    return new RegExp(pattern, "gi");
}

type PDFViewProps = {
    doc: DocumentRead;
    highlightOptions: HighlightOption[];
    searchRef?: MutableRefObject<PdfSearchHandle | null>;
    onReady?: (numPages: number) => void;
    /** Fires when the visible PDF page changes (0-based page index). */
    onPageChange?: (pageIndex: number) => void;
};

const PDFView: React.FC<PDFViewProps> = ({doc, highlightOptions, searchRef, onReady, onPageChange}) => {
    const [documentUrl, setDocumentUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const {navigateTo} = useNavigationContext()!;
    const urlRef = useRef<string>('');

    // defaultLayoutPlugin uses React.useMemo internally — it MUST be called
    // unconditionally every render (it's effectively a hook)
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        toolbarPlugin: {
            searchPlugin: {
                onHighlightKeyword: (props: OnHighlightKeyword) => {
                    props.highlightEle.style.backgroundColor = 'rgba(251, 191, 36, 0.35)';
                    props.highlightEle.style.padding = '2px 0';
                    props.highlightEle.style.borderRadius = '2px';
                    // Leave headroom above when jumpToMatch scrolls this into view
                    props.highlightEle.style.scrollMarginTop = '6rem';
                },
            },
        },
    });

    // Load PDF URL — tries signed URL (direct GCS) first, falls back to proxy
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            const url = await withApiErrorHandlerThrow(
                () => getPdfUrl(doc.id),
                {titleQualifier: "Failed to load document", navigateTo}
            );
            if (cancelled) return;
            // Revoke old blob URL if it was one (signed URLs don't need revoking)
            if (urlRef.current && urlRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(urlRef.current);
            }
            urlRef.current = url;
            setDocumentUrl(url);
            setLoading(false);
        };
        load();
        return () => { cancelled = true; };
    }, [doc.id]);

    // Update highlights programmatically — Viewer stays mounted (no key remount)
    useEffect(() => {
        if (!documentUrl) return;
        try {
            const search = defaultLayoutPluginInstance.toolbarPluginInstance.searchPluginInstance;
            if (!highlightOptions?.length) {
                search.clearHighlights();
                return;
            }
            const keywords = highlightOptions
                .map(option => buildSeamTolerantRegex(option.label) ?? option.label);
            search.highlight(keywords);
        } catch (err) {
            console.warn('[PDFView] highlight update failed:', err);
        }
    }, [highlightOptions, documentUrl]);

    // Expose search + navigation methods to parent.
    // Re-runs on every render because defaultLayoutPlugin is recreated each render,
    // so the plugin instances change.
    const search = defaultLayoutPluginInstance.toolbarPluginInstance.searchPluginInstance;
    const pageNav = defaultLayoutPluginInstance.toolbarPluginInstance.pageNavigationPluginInstance;
    useEffect(() => {
        if (!searchRef) return;
        searchRef.current = {
            highlight: async (text: string, targetPage?: number) => {
                try {
                    const regex = buildSeamTolerantRegex(text);
                    const matches = await search.highlight(regex ?? text);
                    console.log('[PDFView] highlight:', {
                        text: text.slice(0, 80),
                        matchCount: matches.length,
                    });
                    // Diagnostic: if 0 matches, load the target page's text via
                    // PDF.js and test regex directly — bypasses the search plugin
                    if (matches.length === 0 && regex && documentUrl && targetPage != null) {
                        try {
                            const pdfDoc = await pdfjs.getDocument(documentUrl).promise;
                            const page = await pdfDoc.getPage(targetPage); // 1-based
                            const content = await page.getTextContent();
                            const pageText = content.items.map((item) => ('str' in item ? item.str : '') || '').join('');
                            regex.lastIndex = 0;
                            const directMatch = regex.test(pageText);
                            // Also try plain text search
                            const plainMatch = pageText.includes(text);
                            const plainMatchCI = pageText.toLowerCase().includes(text.toLowerCase());
                            console.log(`[PDFView] diagnostic on page ${targetPage}:`, {
                                directMatch,
                                plainMatch,
                                plainMatchCI,
                                pageTextSample: pageText.slice(0, 200),
                                pageTextLength: pageText.length,
                            });
                            // Try just the first few words
                            const firstWords = text.split(/\s+/).slice(0, 4).join(' ');
                            const firstWordsMatch = pageText.toLowerCase().includes(firstWords.toLowerCase());
                            console.log(`[PDFView] first-words test:`, {
                                firstWords,
                                found: firstWordsMatch,
                            });
                        } catch (diagErr) {
                            console.warn('[PDFView] diagnostic failed:', diagErr);
                        }
                    }
                    return matches.length;
                } catch (err) {
                    console.warn('[PDFView] highlight failed:', err);
                    return 0;
                }
            },
            jumpToMatch: (i: number) => { try { search.jumpToMatch(i); } catch { /* noop */ } },
            jumpToNextMatch: () => { try { search.jumpToNextMatch(); } catch { /* noop */ } },
            jumpToPreviousMatch: () => { try { search.jumpToPreviousMatch(); } catch { /* noop */ } },
            clearHighlights: () => { try { search.clearHighlights(); } catch { /* noop */ } },
            jumpToPage: (pageIndex: number) => { try { pageNav.jumpToPage(pageIndex); } catch { /* noop */ } },
        };
        return () => { if (searchRef) searchRef.current = null; };
    }, [searchRef, search, pageNav]);

    return (
        <div className="relative flex-1 min-h-0 h-full" style={{minHeight: 400}}>
            <SpecificComponentLoader active={loading} text="Loading document..."/>
            {documentUrl && (
                <Viewer
                    fileUrl={documentUrl}
                    plugins={[defaultLayoutPluginInstance]}
                    scrollMode={ScrollMode.Vertical}
                    onDocumentLoad={(e) => onReady?.(e.doc.numPages)}
                    onPageChange={(e: PageChangeEvent) => onPageChange?.(e.currentPage)}
                />
            )}
        </div>
    );
};

export default PDFView;
